import {specialBrandSlugs} from './FilterLogic.js';

class FilterRedirectionBuilder {

    urlParts = {};

    constructor( urlParts ) {
        // Set Data.
        this.urlParts = urlParts;
        this.setup();
    }

    setup() {
        // This streamlines all values and makes them workable.
        this.setShop( this.getShop() );
        this.setMarkenshop( this.getMarkenshop() );
        this.setBrands( this.getBrands() );
    }

    getShop() {
        const shop = this.urlParts['shop'] ?? [];
        return [].concat( shop );
    }

    getMarkenshop() {
        const markenshop = this.urlParts['markenshop'] ?? []
        return [].concat( markenshop ).filter( brandname => 'markenshop' !== brandname ).filter( i => i );
    }

    getBrands() {
        const tmpBrands = [].concat( this.urlParts['brand'] ?? [] ).filter( i => i );
        return tmpBrands.filter( (brand, index) => tmpBrands.indexOf( brand ) === index );
    }

    getSortField() {
        const sortField = this.urlParts['sortfield'] ?? [];
        return [].concat( sortField );
    }

    setShop( shop ) {
        if ( null === shop ) {
            delete this.urlParts['shop'];
            return this;
        }
        this.urlParts['shop'] = [].concat( shop );
        return this;
    }

    setMarkenshop( markenshop ) {
        if ( null === markenshop ) {
            delete this.urlParts['markenshop'];
            return this;
        }
        const markenshopNames = [].concat( markenshop ).filter( brandname => 'markenshop' !== brandname ).filter( i => i );
        this.urlParts['markenshop'] = [...markenshopNames, 'markenshop'];
        return this;
    }

    setBrands( brands ) {
        if ( null === brands ) {
            delete this.urlParts['brand']
            return this;
        }

        const tmpBrands = [].concat( brands ).filter( i => i );
        this.urlParts['brand'] = tmpBrands.filter( (brand, index) => tmpBrands.indexOf( brand ) === index );
        return this;
    }

    isNormalShop() {
        return ! isMarkenShop();
    }
    isMarkenShop() {
        const markenshop = this.getMarkenshop();
        return markenshop.length > 0;
    }

    // # First Case - Replace Markenshop with Shop.
    redirectFromMarkenShopToShop = () => {

        // Get values.
        const markenshop = this.getMarkenshop();
        const brands = this.getBrands();
        const shop = this.getShop();

        // Remove Markenshop, because we have more than one brand or a normal category..
        const hasMoreThanOneBrand = markenshop.length > 0 && brands.filter( i => i ).length > 0;
        const hasBothShopAndBrand = markenshop.length > 0 && shop.filter( i => i ).length > 0;

        // In either case, we remove the markenshop and handle as a normal shop call.
        if ( hasMoreThanOneBrand || hasBothShopAndBrand ) {

            // Create new Brand Array that contains all ( truthy and unique ) brandnames.
            this.setShop( this.urlParts['shop'] ?? [''] );
            this.setBrands( [ ...markenshop, ...brands ] );
            this.setMarkenshop( null );
        }

        return this;
    };

    // # Second Case - Replace Shop with Markenshop.
    redirectFromShopToMarkenshop = () => {

        // Get values.
        const shop = this.getShop();
        const brands = this.getBrands();

        // Remove Shop if we have only one brand on the base shop.
        const hasOnlyOneBrand = brands.filter( i => i ).length === 1;
        const hasBaseCatShop = shop.filter( i => i ).length <= 0;

        if ( hasOnlyOneBrand ) {

            const brand = brands[0].replace( /~/g, '-' ).replace( /\^/g, '/' );

            if ( specialBrandSlugs.includes( brand ) ) {

                // Use the /chanel/brand-chanel/ Syntax.
                if ( shop[0] !== brand ) {
                    this.setShop( brand );
                }
                this.setBrands( brand );
                this.setMarkenshop( null )
            } else if ( hasBaseCatShop ) {

                // Assign Brand to Markenshop, and add the "-markenshop" at the end.
                // Remove duplicate Shopinformation, thats now contained in the markenshop.
                this.setShop( null );
                this.setBrands( null );
                this.setMarkenshop( brand )
            }
        }

        return this;
    };

    // # Chanel and Dior need special handling - Append the /brand-chanel/ if its not there yet.
    checkForSpecialBrands() {

        // Get values.
        const brands = this.getBrands();

        // Get current Shop.
        const shop = this.getShop();
        const currentShop = shop[0] ?? null;

        // Special Case for /dior/ and /chanel/ Urls.
        if ( specialBrandSlugs.includes( currentShop ) ) {

            // Always add special Brand to Brandslist - if its a duplicate, it will get filtered automatically.
            const newBrands = this.setBrands( [...brands, currentShop ] ).getBrands();

            if ( newBrands.length > 1 ) {
                // Use normal /shop/
                this.setShop( '' );
            } else {
                // Remove Markenshop - should never be a problem, but just in case.
                this.setMarkenshop( null );
            }
        }


        return this;
    }

    getUrlParts() {
        // Create a copy of the urlParts.
        const urlParts = Object.assign( this.urlParts );

        // Get value of Markenshop once more.
        const newShop = this.getShop();
        const newBrands = this.getBrands();
        const newMarkenshop = this.getMarkenshop()
        const newSort = this.getSortField();

        // Lastly, ensure, that there always is a shop set, even when there is no markenshop.
        // Also sortorder for new is different between shop and markenshop, so always fix that.
        if ( this.isMarkenShop() ) {
            // This is a Markenshop Page.
            // No shop on markenshop pages.
            delete urlParts['shop'];

            // Fix sortorder.
            if ( newSort.length > 0 ) {
                urlParts['sortfield'] = newSort.map( sortValue => {
                    if ( ! sortValue.includes( 'mneu' ) ) {
                        return sortValue.replace( 'neu', 'mneu' );
                    }
                    return sortValue;
                } );
            }

        } else {
            // This is any other shoppage.
            // No Markenshop on normal shoppages.
            delete urlParts['markenshop'];

            if ( newShop.length <= 0 ) {
                urlParts['shop'] = urlParts['shop'] ?? [''];
            }

            // Fix sortorder.
            if ( newSort.length > 0 ) {
                urlParts['sortfield'] = newSort.map( sortValue => sortValue.replace( 'mneu', 'neu' ) );
            }
        }

        if ( newBrands.length <= 0 ) {
            delete urlParts['brand'];
        }

        return urlParts;
    }
}

export default FilterRedirectionBuilder;
